import { useState, useRef, useCallback, useEffect } from 'react';

const LONG_PRESS_DURATION = 400; // ms
const MOVE_THRESHOLD_PX = 10; // Umbral de movimiento para cancelar el long press

/**
 * Activa haptic feedback (vibración) si el dispositivo lo soporta.
 */
const triggerHapticFeedback = () => {
  if (navigator.vibrate) {
    navigator.vibrate(50);
  }
};

/**
 * Hook para detectar long press en dispositivos móviles.
 * Habilita el drag solo después de mantener presionado el elemento.
 * Previene el menú contextual nativo y el scroll durante el drag.
 *
 * @param {boolean} enabled - Si el long press está habilitado
 * @param {number} longPressDuration - Duración en ms para considerar un long press
 * @returns {Object} - { isLongPressed, handlers }
 */
const useLongPress = (enabled = false, longPressDuration = LONG_PRESS_DURATION) => {
  const [isLongPressed, setIsLongPressed] = useState(false);
  const timerRef = useRef(null);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const isLongPressedRef = useRef(false);

  // Prevenimos el scroll a nivel de documento cuando el drag está activo para evitar el scroll al desplazar alguna imagen
  useEffect(() => {
    if (!isLongPressed) return;

    // Guardamos los estilos originales del body
    const originalOverflow = document.body.style.overflow;
    const originalTouchAction = document.body.style.touchAction;

    // Bloqueamos el scroll del body
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.touchAction = originalTouchAction;
    };
  }, [isLongPressed]);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleTouchStart = useCallback(
    e => {
      if (!enabled) return;

      const touch = e.touches[0];
      touchStartPos.current = { x: touch.clientX, y: touch.clientY };

      timerRef.current = setTimeout(() => {
        isLongPressedRef.current = true;
        setIsLongPressed(true);
        triggerHapticFeedback();
      }, longPressDuration);
    },
    [enabled, longPressDuration]
  );

  const handleTouchMove = useCallback(
    e => {
      if (!enabled) return;

      // Si el long press está activo, prevenimos el scroll de la página
      if (isLongPressedRef.current) {
        e.preventDefault();
        return;
      }

      // Si el timer está corriendo (aún no pasaron 400ms) y el usuario mueve el dedo más de 10px, cancelamos porque quiere hacer scroll
      if (timerRef.current) {
        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);
        if (deltaX > MOVE_THRESHOLD_PX || deltaY > MOVE_THRESHOLD_PX) {
          clearTimer();
        }
      }
    },
    [enabled]
  );

  const handleTouchEnd = useCallback(() => {
    clearTimer();
    setIsLongPressed(false);
    isLongPressedRef.current = false;
  }, []);

  const handleContextMenu = useCallback(
    e => {
      if (enabled) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [enabled]
  );

  if (!enabled) {
    return {
      isLongPressed: false,
      handlers: {}
    };
  }

  return {
    isLongPressed,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: handleTouchEnd,
      onContextMenu: handleContextMenu
    }
  };
};

export { useLongPress };
